import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { UFOThirdPartySegment } from '@atlaskit/react-ufo/third-party-segment';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop.ts';
import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import {
	EXTENSION_POINT_HOME_FOOTER_EXPERIENCE,
	EXTENSION_POINT_HOME_HEADER_EXPERIENCE,
	EXTENSION_POINT_EDITOR_SAVEBAR_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
	ATL_EDITOR_SAVEBAR,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { filterConnectAppsFromExtensionPoints } from '@confluence/web-item-location/entry-points/webItemUtils.ts';

import {
	type WebPanelLocationQuery$data,
	type WebPanelLocationQuery$variables,
} from './__generated__/WebPanelLocationQuery.graphql.ts';
import { WebPanel } from './WebPanel.tsx';

export const WebPanelLocationQuery: GraphQLTaggedNode = graphql`
	query WebPanelLocationQuery(
		$location: String
		$locations: [String]
		$contentId: ID
		$spaceKey: String
	) {
		webPanels(location: $location, locations: $locations, contentId: $contentId, key: $spaceKey) {
			moduleKey
			completeKey
			html
			location
			label
			weight
			name
		}
	}
`;

export type WebPanel = {
	completeKey: string;
	html: string;
	weight: number;
	label: string;
};

type WebPanelLocationProps = {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- This is a prop name, not the browser global.
	location: string;
	style?: { [key: string]: string };
	// Please note it is default to "cache-and-network".
	// You can safely put it to "cache-first" only when the data don't change during the entire browser session.
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
	onCompleted?: () => void;
	children?: (props: { panels: WebPanel[] }) => ReactNode;
	renderWhenLoading?: boolean;
};

type WebPanelLocationQueryResult = {
	data?: WebPanelLocationQuery$data | null;
	loading: boolean;
	error?: Error | null;
};

type WebPanelLocationBatchContextValue = {
	locations: string[];
	getResultForLocation: (location: string) => WebPanelLocationQueryResult;
};

type WebPanelLocationBatchProviderProps = {
	locations: string[];
	children: ReactNode;
};

type WebPanelLocationBatchContextProviderProps = WebPanelLocationQueryResult & {
	locations: string[];
	children: ReactNode;
};

const WebPanelLocationBatchContext = createContext<WebPanelLocationBatchContextValue | null>(null);

export const useWebPanelLocationBatchResult = (
	location: string,
): WebPanelLocationQueryResult | null => {
	const batchContext = useContext(WebPanelLocationBatchContext);

	return batchContext?.locations.includes(location)
		? batchContext.getResultForLocation(location)
		: null;
};

const getUniqueLocations = (locations: string[]) => Array.from(new Set(locations.filter(Boolean)));

const groupPanelsByLocation = (webPanels?: WebPanelLocationQuery$data['webPanels'] | null) => {
	type WebPanelQueryResult = NonNullable<
		NonNullable<WebPanelLocationQuery$data['webPanels']>[number]
	>;
	const panelsByLocation = new Map<string, WebPanelQueryResult[]>();

	webPanels?.forEach((panel) => {
		if (panel?.location) {
			const panels = panelsByLocation.get(panel.location) ?? [];
			panels.push(panel);
			panelsByLocation.set(panel.location, panels);
		}
	});

	return panelsByLocation;
};

const getWebPanelLocationVariables = ({
	location,
	locations,
	contentIdentifier,
	spaceIdentifier,
}: {
	location?: string;
	locations?: string[];
	contentIdentifier?: string | null;
	spaceIdentifier?: string | null;
}): WebPanelLocationQuery$variables => {
	const selectedLocations = locations ?? (location ? [location] : []);
	const locationVariables =
		selectedLocations.length > 1
			? { location: null, locations: selectedLocations }
			: { location: selectedLocations[0] };

	return {
		...locationVariables,
		...(contentIdentifier != null ? { contentId: contentIdentifier } : {}),
		...(contentIdentifier == null && spaceIdentifier != null ? { spaceKey: spaceIdentifier } : {}),
	};
};

const useWebPanelLocationIdentifiers = () => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	return {
		spaceIdentifier:
			spaceKey || getSingleParam(queryParams, 'spaceKey') || getSingleParam(queryParams, 'key'),
		contentIdentifier: contentId || getSingleParam(queryParams, 'pageId'),
	};
};

const UFOWebPanelLocationWrapper = ({
	children,
	locationName,
	panels,
}: {
	children: ReactNode;
	locationName: string;
	panels: WebPanel[];
}) => {
	const panelHasContent = panels?.length && panels.some((panel) => panel.html !== '');

	// Record the presence of non-content 3P web-panel apps (header/footer/
	// metadata banner/etc.) so their keys are folded into the page-load
	// `connectAppKeys` telemetry.
	useEffect(() => {
		recordNonContentAppKeys(
			'web-panel',
			(panels ?? []).map((panel) => panel.completeKey),
		);
	}, [panels]);

	if (!panelHasContent) {
		return children;
	}

	return <UFOThirdPartySegment name={`web-panel-${locationName}`}>{children}</UFOThirdPartySegment>;
};

const MemoizedAtlGeneralWebPanel = React.memo(
	({ panel }: { panel: WebPanel }) => (
		<WebPanel panelId={panel.completeKey} html={panel.html} panelLocation={ATL_GENERAL} />
	),
	(prevProps, nextProps) => prevProps.panel.completeKey === nextProps.panel.completeKey,
);

const WebPanelLocationBatchContextProvider = ({
	data,
	loading,
	error,
	locations,
	children,
}: WebPanelLocationBatchContextProviderProps) => {
	const panelsByLocation = useMemo(() => groupPanelsByLocation(data?.webPanels), [data?.webPanels]);
	const contextValue = useMemo<WebPanelLocationBatchContextValue>(
		() => ({
			locations,
			getResultForLocation: (location) => ({
				data: data
					? {
							webPanels: panelsByLocation.get(location) ?? [],
						}
					: data,
				loading,
				error,
			}),
		}),
		[data, error, loading, locations, panelsByLocation],
	);

	return (
		<WebPanelLocationBatchContext.Provider value={contextValue}>
			{children}
		</WebPanelLocationBatchContext.Provider>
	);
};

export const WebPanelLocationBatchProvider = ({
	locations,
	children,
}: WebPanelLocationBatchProviderProps): React.JSX.Element => {
	const { spaceIdentifier, contentIdentifier } = useWebPanelLocationIdentifiers();
	const locationsKey = locations.join('\0');
	const uniqueLocations = useMemo(
		() => getUniqueLocations(locationsKey.split('\0')),
		[locationsKey],
	);
	const variables = useMemo(
		() =>
			getWebPanelLocationVariables({
				locations: uniqueLocations,
				contentIdentifier,
				spaceIdentifier,
			}),
		[contentIdentifier, spaceIdentifier, uniqueLocations],
	);

	if (spaceIdentifier === 'byDraft' || uniqueLocations.length === 0) {
		return <>{children}</>;
	}

	return (
		<Query<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>
			query={WebPanelLocationQuery}
			variables={variables}
			fetchPolicy="cache-first"
		>
			{({ data, loading, error }) => (
				<WebPanelLocationBatchContextProvider
					data={data}
					loading={loading}
					error={error}
					locations={uniqueLocations}
				>
					{children}
				</WebPanelLocationBatchContextProvider>
			)}
		</Query>
	);
};

export const WebPanelLocation = ({
	location,
	style = {},
	renderWhenLoading,
	// Query is preloaded on the server-side. reused the preloaded data.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	fetchPolicy = process.env.REACT_SSR ? 'cache-first' : 'cache-and-network',
	children,
	onCompleted,
}: WebPanelLocationProps): React.JSX.Element | null => {
	const { spaceIdentifier, contentIdentifier } = useWebPanelLocationIdentifiers();
	const batchResult = useWebPanelLocationBatchResult(location);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const completedBatchResultKeyRef = useRef<string | null>(null);

	const EXPERIENCE_NAME_BY_LOCATION = {
		[ATL_FOOTER]: EXTENSION_POINT_HOME_FOOTER_EXPERIENCE,
		[ATL_GENERAL]: EXTENSION_POINT_HOME_HEADER_EXPERIENCE,
		[ATL_EDITOR_SAVEBAR]: EXTENSION_POINT_EDITOR_SAVEBAR_EXPERIENCE,
	};

	const experienceName = EXPERIENCE_NAME_BY_LOCATION[location] || null;

	useEffect(() => {
		if (experienceName) {
			experienceTracker.start({ name: experienceName });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const variables = getWebPanelLocationVariables({
		location,
		contentIdentifier,
		spaceIdentifier,
	});

	useEffect(() => {
		if (batchResult?.loading || batchResult?.error || !batchResult?.data || !onCompleted) {
			return;
		}

		const batchResultKey = `${contentIdentifier || ''}:${spaceIdentifier || ''}:${location}`;
		if (completedBatchResultKeyRef.current !== batchResultKey) {
			completedBatchResultKeyRef.current = batchResultKey;
			onCompleted();
		}
	}, [batchResult, contentIdentifier, location, onCompleted, spaceIdentifier]);

	// TODO: The task to remove this hack is captured in CFE-1873.
	// There's some weird scenario where the UI ends up with a space key of "byDraft" relating to the way tinymce
	// creates content. Given that it's tinymce specific, I'm applying this hack for now and we can remove it once we're
	// at 100% rollout for the Fabric editor.
	if (spaceIdentifier === 'byDraft') return null;

	const renderWebPanels = ({ data, loading, error }: WebPanelLocationQueryResult) => {
		if (!renderWhenLoading) {
			if (error || !data || (loading && !fg('confluence_web_panel_location_render_loading'))) {
				return null;
			}
		}

		let panels = (data?.webPanels || []).filter((panel) => panel) as WebPanel[];
		panels = filterConnectAppsFromExtensionPoints(location, panels);

		// For panels that don't have a placeholder
		if (!renderWhenLoading && !panels.length) {
			if (experienceName) {
				experienceTracker.succeed({
					name: experienceName,
				});
			}
			return null;
		}

		const shouldMemoizeAtlGeneralWebPanel =
			location === ATL_GENERAL &&
			expValEquals('confluence_memorize_atl_general_connect', 'isEnabled', true);

		return (
			<>
				<UFOWebPanelLocationWrapper locationName={location} panels={panels}>
					{children
						? children({ panels })
						: panels.map((panel) => {
								return shouldMemoizeAtlGeneralWebPanel ? (
									<MemoizedAtlGeneralWebPanel key={panel.completeKey} panel={panel} />
								) : (
									<WebPanel
										key={panel.completeKey}
										panelId={panel.completeKey}
										html={panel.html}
										panelLocation={location}
									/>
								);
							})}
				</UFOWebPanelLocationWrapper>
				{experienceName ? <ExperienceSuccess name={experienceName} /> : null}
			</>
		);
	};

	return (
		<div
			/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
			style={style}
			data-vc-ignore-if-no-layout-shift
		>
			{batchResult ? (
				renderWebPanels(batchResult)
			) : (
				<Query<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>
					query={WebPanelLocationQuery}
					variables={variables}
					fetchPolicy={fetchPolicy}
					onCompleted={onCompleted}
				>
					{renderWebPanels}
				</Query>
			)}
		</div>
	);
};
